<script>
import QRCode from 'qrcode';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappFreeChannelAPI, {
  extractResponseError,
} from 'dashboard/api/channel/whatsappFree';

const POLL_INTERVAL = 3000;
const KNOWN_STATUS_KEYS = [
  'STARTING',
  'CONNECTING',
  'QR',
  'CONNECTED',
  'DISCONNECTED',
  'UNKNOWN',
];

export default {
  components: {
    PageHeader,
    NextButton,
    Spinner,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      setup: null,
      runtime: null,
      qrImage: '',
      lastQrValue: '',
      pollTimer: null,
      isStarting: false,
      isPolling: false,
      isFinalizing: false,
      isReconnecting: false,
      isCancelling: false,
      isFinalized: false,
      errorMessage: '',
    };
  },
  computed: {
    sessionStatus() {
      return this.runtime?.session?.status || 'starting';
    },
    statusLabel() {
      const key = String(this.sessionStatus || 'starting').toUpperCase();
      const safeKey = KNOWN_STATUS_KEYS.includes(key) ? key : 'UNKNOWN';
      return this.$t(`INBOX_MGMT.ADD.WHATSAPP_FREE.STATUS.${safeKey}`);
    },
    setupId() {
      return this.setup?.id;
    },
  },
  validations: {
    channelName: { required },
  },
  beforeUnmount() {
    this.stopPolling();
    if (this.setupId && !this.isFinalized) {
      WhatsappFreeChannelAPI.setupDestroy(this.setupId).catch(() => {});
    }
  },
  methods: {
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async renderQr(qr) {
      if (!qr) {
        this.lastQrValue = '';
        this.qrImage = '';
        return;
      }
      if (qr === this.lastQrValue) return;

      this.lastQrValue = qr;
      this.qrImage = await QRCode.toDataURL(qr, {
        width: 288,
        margin: 2,
      });
    },
    async startSetup() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.isStarting = true;
      this.errorMessage = '';

      try {
        const response = await WhatsappFreeChannelAPI.createInbox({
          name: this.channelName.trim(),
        });
        this.setup = response.setup;
        this.runtime = response.runtime;
        this.startPolling();
      } catch (error) {
        this.errorMessage = extractResponseError(error);
        useAlert(this.errorMessage);
      } finally {
        this.isStarting = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.refreshSetup();
      this.pollTimer = setInterval(() => this.refreshSetup(), POLL_INTERVAL);
    },
    async refreshSetup() {
      if (!this.setupId || this.isPolling || this.isFinalizing) return;

      this.isPolling = true;
      try {
        const response = await WhatsappFreeChannelAPI.setupQr(this.setupId);
        this.runtime = response.runtime;
        await this.renderQr(response.qr);
        this.errorMessage = '';

        if (this.sessionStatus === 'connected') {
          await this.finalizeSetup();
        }
      } catch (error) {
        this.errorMessage = extractResponseError(error);
      } finally {
        this.isPolling = false;
      }
    },
    async reconnectSetup() {
      if (!this.setupId) return;

      this.isReconnecting = true;
      this.errorMessage = '';
      try {
        const response = await WhatsappFreeChannelAPI.setupReconnect(
          this.setupId
        );
        this.runtime = response.runtime;
        this.qrImage = '';
        this.lastQrValue = '';
        this.startPolling();
      } catch (error) {
        this.errorMessage = extractResponseError(error);
        useAlert(this.errorMessage);
      } finally {
        this.isReconnecting = false;
      }
    },
    async finalizeSetup() {
      if (!this.setupId || this.isFinalizing) return;

      this.isFinalizing = true;
      this.errorMessage = '';

      try {
        const response = await WhatsappFreeChannelAPI.setupFinalize(
          this.setupId
        );
        this.isFinalized = true;
        this.stopPolling();
        this.$router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: response.inbox.id,
          },
        });
      } catch (error) {
        this.errorMessage = extractResponseError(error);
        useAlert(this.errorMessage);
      } finally {
        this.isFinalizing = false;
      }
    },
    async cancelSetup() {
      if (!this.setupId) {
        this.$router.replace({ name: 'settings_inbox_new' });
        return;
      }

      this.isCancelling = true;
      try {
        await WhatsappFreeChannelAPI.setupDestroy(this.setupId);
        this.isFinalized = true;
        this.stopPolling();
        this.$router.replace({ name: 'settings_inbox_new' });
      } catch (error) {
        this.errorMessage = extractResponseError(error);
        useAlert(this.errorMessage);
      } finally {
        this.isCancelling = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-auto">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.DESC')"
    />

    <form
      v-if="!setup"
      class="flex flex-col max-w-2xl"
      @submit.prevent="startSetup"
    >
      <label :class="{ error: v$.channelName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_FREE.CHANNEL_NAME.LABEL') }}
        <input
          v-model="channelName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP_FREE.CHANNEL_NAME.PLACEHOLDER')
          "
          @blur="v$.channelName.$touch"
        />
        <span v-if="v$.channelName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_FREE.CHANNEL_NAME.ERROR') }}
        </span>
      </label>

      <div class="w-full mt-4">
        <NextButton
          type="submit"
          solid
          blue
          icon="i-lucide-smartphone"
          :is-loading="isStarting"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.START_BUTTON')"
        />
      </div>
    </form>

    <div v-else class="max-w-4xl">
      <div
        class="flex flex-col md:flex-row gap-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak p-6 bg-n-solid-1"
      >
        <div
          class="flex items-center justify-center flex-shrink-0 w-72 h-72 rounded-xl bg-white outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <img
            v-if="qrImage"
            :src="qrImage"
            :alt="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.QR_ALT')"
            class="w-64 h-64"
          />
          <div v-else class="flex flex-col items-center gap-3 text-n-slate-11">
            <Spinner class="!w-6 !h-6" />
            <span class="text-sm">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP_FREE.WAITING_QR') }}
            </span>
          </div>
        </div>

        <div class="flex flex-col justify-between min-w-0 flex-1 gap-6">
          <div>
            <h2 class="mb-2 text-lg font-medium text-n-slate-12">
              {{ setup.inbox_name }}
            </h2>
            <p class="mb-4 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP_FREE.SCAN_HINT') }}
            </p>

            <div
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12"
            >
              <span class="w-2 h-2 rounded-full bg-n-brand" />
              <span>{{ statusLabel }}</span>
            </div>

            <p v-if="errorMessage" class="mt-4 text-sm text-n-ruby-11">
              {{ errorMessage }}
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-3">
            <NextButton
              type="button"
              outline
              slate
              icon="i-lucide-refresh-cw"
              :is-loading="isReconnecting"
              :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.RECONNECT_BUTTON')"
              @click="reconnectSetup"
            />
            <NextButton
              v-if="sessionStatus === 'connected' || errorMessage"
              type="button"
              solid
              blue
              icon="i-lucide-check"
              :is-loading="isFinalizing"
              :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.FINALIZE_BUTTON')"
              @click="finalizeSetup"
            />
            <NextButton
              type="button"
              ghost
              ruby
              icon="i-lucide-x"
              :is-loading="isCancelling"
              :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.CANCEL_BUTTON')"
              @click="cancelSetup"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
